import React, { useState } from "react";
import { useFela } from "react-fela";
import { useForm } from "react-hook-form";
import useTranslation from "next-translate/useTranslation";

import { useTheme } from "@/hooks/useTheme";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { Radio } from "@/components/Radio";
import { Textarea } from "@/components/Textarea";
import { Image } from "@/components/Image";
import { StyledIconWrapper, Icon } from "@/components/Icon";
import { emailRegexp } from "@/utils/validateEmail";
import { sendContactForm } from "@/services/contactService";

import { IInspectorContactFormData } from "../types";
import { Label } from "./Label";
import { ErrorMessage } from "./ErrorMessage";
import { Checkbox } from "./Checkbox";

type IStatus = "IDLE" | "SUCCESS" | "ERROR" | "PENDING";

export const ContactForm: React.FC<{}> = () => {
  const { bp, colors } = useTheme();
  const { css } = useFela();
  const { t } = useTranslation("section/contact");

  const [status, setStatus] = useState<IStatus>("IDLE");

  const {
    register,
    handleSubmit,
    errors,
    reset,
    getValues,
  } = useForm<IInspectorContactFormData>({
    defaultValues: {
      usageType: [],
    },
  });

  const isSuccess = status === "SUCCESS";
  const isError = status === "ERROR";
  const isPending = status === "PENDING";

  const handleFormSubmit = handleSubmit(data => {
    setStatus("PENDING");

    sendContactForm(data);

    reset();
    setStatus("SUCCESS");
  });

  return (
    <>
      <form
        id="inspector-contact-form"
        onSubmit={isPending ? undefined : handleFormSubmit}
        className={css({
          width: "100%",
          [bp.FROM_TABLET]: {
            maxWidth: 970,
            display: "flex",
            flexDirection: "row",
          },
        })}
      >
        <div className={css({ flex: 1 })}>
          <h5
            className={css({
              margin: 0,
              fontSize: 24,
              lineHeight: "32px",
              marginBottom: 24,
              color: colors.DARK_GREY,
              fontWeight: 700,
            })}
          >
            {t("describeYourself")}
          </h5>

          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              marginBottom: 24,
            })}
          >
            <Label htmlFor="name" text={t("fields.name")} />
            <Input
              type="text"
              placeholder={t("fields.name")}
              name="name"
              id="name"
              ref={register({
                required: {
                  value: true,
                  message: "name",
                },
              })}
              customStyle={{
                width: "100%",
                [bp.FROM_TABLET]: {
                  maxWidth: 370,
                },
                ...(errors.name ? { borderColor: colors.RED } : {}),
              }}
            />

            {errors.name && (
              <ErrorMessage text={t(`errors.${errors.name.message}`)} />
            )}
          </div>
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              marginBottom: 24,
            })}
          >
            <Label htmlFor="email" text={t("fields.email")} />
            <Input
              type="email"
              placeholder={t("emailPlaceholder")}
              id="email"
              name="email"
              ref={register({
                required: {
                  value: true,
                  message: "emailEmpty",
                },
                pattern: {
                  value: emailRegexp,
                  message: "emailInvalid",
                },
              })}
              customStyle={{
                width: "100%",
                [bp.FROM_TABLET]: {
                  maxWidth: 370,
                },
                ...(errors.name ? { borderColor: colors.RED } : {}),
              }}
            />

            {errors.email && (
              <ErrorMessage text={t(`errors.${errors.email.message}`)} />
            )}
          </div>

          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              nested: {
                "& label:first-child": {
                  marginBottom: 0,
                },
                "& label": {
                  marginTop: 10,
                },
                "& input:checked + span:before": {
                  backgroundColor: `${colors.GRADIENT_PURPLE} !important`,
                },
                "& label span": {
                  color: `${colors.DARK_GREY} !important`,
                  fontSize: 16,
                  lineHeight: "24px",
                  fontWeight: 600,
                },
                "& label span:before": {
                  top: 2,
                },
                "& label span:after": {
                  top: 2,
                },
              },
            })}
          >
            <Label htmlFor="userType" text={t("fields.userType")} />
            {errors.userType && <ErrorMessage text={t(`errors.userType`)} />}

            <Radio
              id="userType"
              name="userType"
              value="Agency"
              label={t("fields.agency")}
              ref={register({ required: true })}
            />
            <Radio
              name="userType"
              value="Product Team"
              label={t("fields.productTeam")}
              ref={register({ required: true })}
            />
            <Radio
              name="userType"
              value="Freelancer"
              label={t("fields.freelancer")}
              ref={register({ required: true })}
            />
            <Radio
              name="userType"
              value="Other"
              label={t("fields.other")}
              ref={register({ required: true })}
            />

            <Input
              type="text"
              placeholder={t("workPlaceholder")}
              name="userTypeDescription"
              ref={register({
                validate: (value: string) => {
                  if (getValues().userType === "Other" && value.length === 0) {
                    return false;
                  }

                  return true;
                },
              })}
              customStyle={{
                width: "100%",
                marginTop: 16,
                [bp.FROM_TABLET]: {
                  maxWidth: 370,
                },
                ...(errors.userTypeDescription
                  ? { borderColor: colors.RED }
                  : {}),
              }}
            />
            {errors.userTypeDescription && (
              <ErrorMessage text={t(`errors.userTypeDescription`)} />
            )}
          </div>
        </div>

        <div
          className={css({
            flex: 1,
            marginTop: 30,
            [bp.FROM_TABLET]: {
              marginTop: 0,
              marginLeft: 130,
            },
          })}
        >
          <h5
            className={css({
              margin: 0,
              fontSize: 24,
              lineHeight: "32px",
              marginBottom: 24,
              color: colors.DARK_GREY,
              fontWeight: 700,
            })}
          >
            {t("whatFor")}
          </h5>

          <div
            className={css({
              display: "flex",
              flexDirection: "column",
            })}
          >
            {errors.usageType && (
              <ErrorMessage
                text={t(`errors.usageType`)}
                customStyle={{
                  marginTop: 0,
                  marginBottom: 10,
                }}
              />
            )}
            <Checkbox
              name="usageType"
              value="Developer Handoff"
              label={t("fields.developerHandoff")}
              ref={register({ required: true })}
            />
            <Checkbox
              name="usageType"
              value="Downloading Assets"
              label={t("fields.downloadingAssets")}
              ref={register({ required: true })}
            />
            <Checkbox
              name="usageType"
              value="Edit XD files online"
              label={t("fields.editXDFiles")}
              ref={register({ required: true })}
            />
            <Checkbox
              name="usageType"
              value="Other"
              label={t("fields.other")}
              ref={register({ required: true })}
            />

            <Textarea
              name="usageTypeDescription"
              ref={register({
                validate: (value: string) => {
                  if (
                    getValues().usageType.includes("Other") &&
                    value.length === 0
                  ) {
                    return false;
                  }

                  return true;
                },
              })}
              rows={5}
              placeholder={t("beDetailed")}
              customStyle={{
                resize: "none",
                margin: "6px 0 0",
                color: colors.DARK_GREY,
                nested: {
                  "::placeholder": {
                    color: "#A2A7AC",
                  },
                },

                ...(errors.usageTypeDescription
                  ? { borderColor: colors.RED }
                  : {}),
              }}
            />
            {errors.usageTypeDescription && (
              <ErrorMessage text={t(`errors.usageTypeDescription`)} />
            )}

            <Button
              type="submit"
              disabled={isPending}
              customStyle={{
                marginTop: 24,
                width: 115,
                padding: "10px 24px",
                backgroundColor: colors.ORANGE,
                color: colors.WHITE,
              }}
            >
              {t("submit")}
            </Button>
          </div>
        </div>
      </form>

      <div
        className={css({
          flex: 1,
          marginTop: 50,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          [bp.FROM_TABLET]: {
            marginTop: 0,
          },
        })}
      >
        {isPending && (
          <Image
            alt="Magician"
            src="/svgs/magician-single.svg"
            customStyle={{
              [bp.FROM_TABLET]: { transform: "translateY(-15px)" },
            }}
          />
        )}

        {isSuccess && (
          <div
            id="salesInquiryForm__success"
            className={css({
              marginTop: 40,
              maxWidth: 190,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            })}
          >
            <StyledIconWrapper
              height={70}
              width={70}
              wrapperStyle={{ marginBottom: 20 }}
            >
              <Icon iconType="CHECK_2" />
            </StyledIconWrapper>

            <p
              className={css({
                fontSize: 14,
                color: colors.DARK_GREY,
                letterSpacing: 0.5,
                textAlign: "center",
                lineHeight: 1.7,
              })}
            >
              {t("submitted")}
            </p>
          </div>
        )}

        {isError && (
          <StyledIconWrapper height={70} width={70}>
            <Icon iconType="ERROR-ICON" />
          </StyledIconWrapper>
        )}
      </div>
    </>
  );
};
